import { useState, useCallback, useRef } from "react";
import { useAtom } from "jotai";
import flotteService from "../../../../services/flotteService";
import excelExportService from "../../../../services/excelExportService";
import { flotteFiltersAtom, pageSize } from "./useFlotte";
import { visibleHeadersAtom } from "../components/FlotteTable/useFlotteTable";
import { columnsConfig } from "../components/FlotteTable/flotteColumns.config";
import { toast } from "react-toastify";
import { transformMovements } from "./useFlotte";
import { connectedUserAtom } from "../../../../middlewares/AuthMiddleware";
import { isVORole } from "../components/FlotteTable/FlotteUtils";

// Créer la liste des headers exportables à partir de columnsConfig et visibleHeaders
const getExportableHeaders = (visibleHeaders) => {
  return Object.keys(columnsConfig)
    .filter((field) => visibleHeaders[field] && field !== "actions") // Exclure les colonnes d'actions
    .map((field) => ({
      field,
      title: `${
        columnsConfig[field].badge ? columnsConfig[field].badge + "_" : ""
      } ${columnsConfig[field].title}`,
      path: columnsConfig[field].path,
      type: columnsConfig[field].type,
    }));
};

/**
 * Hook pour gérer l'export Excel de la flotte
 */
const useFlotteExport = () => {
  const [connectedUser] = useAtom(connectedUserAtom);
  const [filters] = useAtom(flotteFiltersAtom);
  const [visibleHeaders] = useAtom(visibleHeadersAtom);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    current: 0,
    total: 0,
  });
  const exportCancelledRef = useRef(false);

  /**
   * Récupère toutes les données de flotte par batch successifs
   * @param {Object} exportFilters - Filtres à appliquer
   * @returns {Array} Toutes les données de flotte
   */
  const fetchAllFlotteData = useCallback(async (exportFilters) => {
    let currentPage = 1;
    let allData = [];
    let totalPages = null;
    let totalItems = null;

    try {
      while (totalPages === null || currentPage <= totalPages) {
        // Vérifier si l'export a été annulé
        console.log(
          "Vérification annulation - exportCancelledRef.current:",
          exportCancelledRef.current,
          "currentPage:",
          currentPage,
        );
        if (exportCancelledRef.current) {
          console.log("Export annulé par l'utilisateur");
          throw new Error("Export annulé par l'utilisateur");
        }

        setExportProgress({
          current: currentPage,
          total: totalPages ? `${totalPages} pages` : "?",
        });

        let response = null;

        if (isVORole(connectedUser.role)) {
          response = await flotteService.getFlotteListVO({
            ...exportFilters,
            page: currentPage,
            pageSize,
          });
        } else {
          response = await flotteService.getFlotteList({
            ...exportFilters,
            page: currentPage,
            pageSize,
          });
        }

        const rawItems = Array.isArray(response?.data) ? response.data : [];

        // Utiliser les métadonnées de pagination pour déterminer le nombre total de pages
        if (response?.meta) {
          totalItems = response.meta.total || response.meta.totalItems;
          if (totalItems && totalPages === null) {
            totalPages = Math.ceil(totalItems / pageSize);
            console.log(
              `Export: ${totalItems} véhicules au total, ${totalPages} pages à récupérer`,
            );
          }
        }

        if (rawItems.length === 0) {
          break; // Plus de données
        }

        // Appliquer les mêmes transformations que le tableau
        const transformedItems = rawItems.map(transformMovements);
        console.log("transformedItems", transformedItems);
        allData = [...allData, ...transformedItems];
        console.log(
          `Page ${currentPage}: ${rawItems.length} véhicules récupérés (total: ${allData.length})`,
        );

        currentPage++;

        // Petite pause pour éviter de surcharger le serveur
        if (currentPage <= (totalPages || currentPage)) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      setExportProgress({
        current: totalPages || currentPage - 1,
        total: `${totalPages || currentPage - 1} pages`,
      });

      console.log(
        `Export terminé: ${allData.length} véhicules récupérés au total`,
      );
      return allData;
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
      throw new Error(
        `Erreur lors de la récupération des données: ${error.message}`,
      );
    }
  }, []);

  /**
   * Annule l'export en cours
   */
  const cancelExport = useCallback(() => {
    console.log("cancelExport appelé, isExporting:", isExporting);
    if (isExporting) {
      console.log("Définition du flag exportCancelledRef.current à true");
      exportCancelledRef.current = true;
      toast.info("Annulation de l'export en cours...");
    }
  }, [isExporting]);

  /**
   * Exporte toutes les données de flotte au format Excel
   * @param {String} filename - Nom du fichier (optionnel)
   */
  const exportFlotteToExcel = useCallback(
    async (filename = "export-flotte") => {
      if (isExporting) {
        toast.warn("Un export est déjà en cours...");
        return;
      }

      setIsExporting(true);
      exportCancelledRef.current = false; // Réinitialiser le flag d'annulation
      setExportProgress({ current: 0, total: 0 });

      try {
        toast.info("Début de l'export Excel...", { autoClose: 2000 });

        // Récupérer toutes les données
        const allData = await fetchAllFlotteData(filters);

        if (allData.length === 0) {
          toast.warn("Aucune donnée à exporter");
          return;
        }

        toast.info(`Export de ${allData.length} lignes en cours...`, {
          autoClose: 2000,
        });

        // Créer la liste des headers exportables à partir de columnsConfig et visibleHeaders
        const exportableHeaders = getExportableHeaders(visibleHeaders);

        console.log("exportableHeaders", exportableHeaders);

        // Exporter vers Excel
        const result = excelExportService.exportToExcel(
          allData,
          exportableHeaders,
          filename,
        );

        if (result.success) {
          toast.success(`Export réussi: ${result.filename}`);
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Erreur lors de l'export Excel:", error);
        toast.error(`Erreur lors de l'export: ${error.message}`);
      } finally {
        setIsExporting(false);
        exportCancelledRef.current = false; // Réinitialiser le flag d'annulation
        setExportProgress({ current: 0, total: 0 });
      }
    },
    [filters, visibleHeaders, isExporting, fetchAllFlotteData],
  );

  /**
   * Exporte les données actuellement affichées (avec pagination)
   */
  const exportCurrentPageToExcel = useCallback(
    async (currentData, filename = "export-flotte-page") => {
      if (isExporting) {
        toast.warn("Un export est déjà en cours...");
        return;
      }

      setIsExporting(true);

      try {
        if (currentData.length === 0) {
          toast.warn("Aucune donnée à exporter");
          return;
        }

        // Créer la liste des headers exportables à partir de columnsConfig et visibleHeaders
        const exportableHeaders = getExportableHeaders(visibleHeaders);

        // Exporter vers Excel
        const result = excelExportService.exportToExcel(
          currentData,
          exportableHeaders,
          filename,
        );

        if (result.success) {
          toast.success(`Export de la page réussi: ${result.filename}`);
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Erreur lors de l'export Excel:", error);
        toast.error(`Erreur lors de l'export: ${error.message}`);
      } finally {
        setIsExporting(false);
      }
    },
    [visibleHeaders, isExporting],
  );

  return {
    isExporting,
    exportProgress,
    exportFlotteToExcel,
    exportCurrentPageToExcel,
    cancelExport,
    fetchAllFlotteData,
  };
};

export default useFlotteExport;
